import {SagaIterator} from 'redux-saga';
import {select, call, put} from 'redux-saga/effects';

import {AddressType} from 'desktop/types/addressType';
import {CustomerAddressType} from 'desktop/types/customerAddressesType';
import {StatesItemType} from 'types/locationType';
import {VoidFunctionType} from 'types/types';

import {saveBillingAddress} from '../../../reducers/deliveryStep/billingAddress/billingAddress';
import {saveShippingAddress} from '../../../reducers/deliveryStep/shippingAddress/shippingAddress';
import {getCustomerAddresses} from '../../../selectors/deliveryStep/deliveryStep';
import {
    applyAssociations,
    customerAddressAssociations,
} from '../decomposeAllAddressesByComponents/utils/applyAssociations/applyAssociations';
import {getStatesForCountry} from '../getStatesForCountry/getStatesForCountry';

import {getRegionCodeModifier} from './utils';

type TestType = {
    address: AddressType;
    save: VoidFunctionType<AddressType>;
};

type UnmodifiedAddressType = {
    address: CustomerAddressType;
    states: Array<StatesItemType>;
    save: VoidFunctionType<AddressType>;
};

/**
 * Saves received modified addresses to store
 * @param {Array<AddressType>} addresses - Received addresses array to save
 */
export function* saveUpdatedAddressesSaga(addresses: Array<TestType>): SagaIterator {
    for (let index = 0; index < addresses.length; index++) {
        const {address, save} = addresses[index];
        const saveAction = yield call(save, address);
        yield put(saveAction);
    }
}

/**
 * Perform addresses modification before save them to store
 * @param {Array<UnmodifiedAddressType>} addresses - Addresses list to modification and save
 */
export function* modifyAddresses(addresses: Array<UnmodifiedAddressType>): SagaIterator {
    const addressesToSave: TestType[] = [];
    for (let index = 0; index < addresses.length; index++) {
        const {address, save, states} = addresses[index];
        const modAddress = yield call<typeof applyAssociations>(
            applyAssociations,
            customerAddressAssociations,
            address,
            {
                regionCode: getRegionCodeModifier(states),
            },
        );
        const modifiedAddress: TestType = {address: modAddress, save};

        yield call([addressesToSave, 'push'], modifiedAddress);
    }
    yield call(saveUpdatedAddressesSaga, addressesToSave);
}

/**
 * Perform restoring of edited customer address after canceled editing of it
 */
export function* restoreAddressesAfterCanceledEditingIfNeededSaga(): SagaIterator {
    const {
        addresses,
        billing_address_id: billingID,
        shipping_address_id: shippingID,
    } = yield select(getCustomerAddresses);
    const customerShippingAddress = addresses.find(
        (address: CustomerAddressType): boolean => address.addressId === shippingID,
    );
    const customerBillingAddress = addresses.find(
        (address: CustomerAddressType): boolean => address.addressId === billingID,
    );
    const shippingAddressStates = yield call(getStatesForCountry, customerShippingAddress.country);
    const billingAddressStates = yield call(getStatesForCountry, customerBillingAddress.country);

    yield call<typeof modifyAddresses>(modifyAddresses, [
        {
            address: customerShippingAddress,
            save: saveShippingAddress,
            states: shippingAddressStates,
        },
        {
            address: customerBillingAddress,
            save: saveBillingAddress,
            states: billingAddressStates,
        },
    ]);
}
